/**
 * CaseDataTypeAllChart Component
 *
 * Same visual layout and controls as CaseDataTypeChart but uses the
 * `/analytics/case-analytics-all` hooks for fetching and exporting data.
 */

import React, { useState, useMemo, useEffect } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Bar } from "react-chartjs-2";
import {
  Box,
  Paper,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  FormControlLabel,
  Switch,
  Button,
  CircularProgress
} from "@mui/material";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { FileDownloadOutlined } from "@mui/icons-material";
import { toast } from "react-hot-toast";
import { metricsConfig, chartStyles, colorUtils } from "../utility/graph";
import { useExportGetCaseAnalyticsAll } from "../../../../../../../features/analytics/hooks/exports/useExportGetCaseAnalyticsAll";
import { useGetCaseAnalyticsAllRange } from "../../../../../../../features/analytics/hooks/useGetCaseAnalyticsAll";
import {
  DATA_TYPE_OPTIONS,
  generateQueryParams,
  getDataTypeValue,
} from "../constants/dataTypeConstants";
import DateRangePicker from "../../../components/date-range-picker";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
);

export const CaseDataTypeAllChart = ({
  title = "Case Analytics (All)",
  initialFromDate = dayjs().startOf("month"),
  initialToDate = dayjs().endOf("month"),
  initialDataType = "all",
  onDataTypeChange,
  dataTypeOptions = DATA_TYPE_OPTIONS,
  refreshKey = 0,
}) => {
  const getInitialLabel = () => {
    if (initialDataType === "all") return "All";
    const option = dataTypeOptions.find((o) => o.value === initialDataType);
    return option?.label || dataTypeOptions[0].label;
  };

  const [fromDate, setFromDate] = useState(
    initialFromDate ? dayjs(initialFromDate) : dayjs().startOf("month"),
  );
  const [toDate, setToDate] = useState(
    initialToDate ? dayjs(initialToDate) : dayjs().endOf("month"),
  );
  const [localDataType, setLocalDataType] = useState(getInitialLabel());
  const [childRefreshKey, setChildRefreshKey] = useState(0);
  const [viewMode, setViewMode] = useState("count");
  const [showDataLabels, setShowDataLabels] = useState(true);

  const exportHook = useExportGetCaseAnalyticsAll({
    onSuccess: (data) => {
      toast.success(
        `Case analytics (all) exported successfully! File: ${data.filename}`,
      );
    },
    onError: (err) => {
      toast.error(`Export failed: ${err.message}`);
    },
  });

  const queryParams = useMemo(() => {
    const params = {
      from: fromDate.format("YYYY-MM-DD"),
      to: toDate.format("YYYY-MM-DD"),
    };
    return generateQueryParams(params, localDataType);
  }, [fromDate, toDate, localDataType]);

  // Always refetch on mount by using a unique key (forces remount/fetch)
  const [fetchKey] = useState(() => Date.now());
  const {
    data: backendData,
    refetch,
    isLoading,
  } = useGetCaseAnalyticsAllRange(queryParams, fetchKey);

  const data = useMemo(() => {
    if (!backendData?.data?.categories) return {};
    const categories = backendData.data.categories;
    return {
      allocated: categories.allocated?.count || 0,
      allocatedPercentage: categories.allocated?.percentage || 0,
      attempted: categories.attempted?.count || 0,
      attemptedPercentage: categories.attempted?.percentage || 0,
      connected: categories.connected?.count || 0,
      connectedPercentage: categories.connected?.percentage || 0,
      unreached: categories.unreached?.count || 0,
      unreachedPercentage: categories.unreached?.percentage || 0,
      childAboveTwo: categories.childAbove2Years?.count || 0,
      childAboveTwoPercentage: categories.childAbove2Years?.percentage || 0,
      notInPortal: categories.notInPortal?.count || 0,
      notInPortalPercentage: categories.notInPortal?.percentage || 0,
      pending: categories.pending?.count || 0,
      pendingPercentage: categories.pending?.percentage || 0,
      active: categories.active?.count || 0,
      activePercentage: categories.active?.percentage || 0,
      unattempted: categories.unattempted?.count || 0,
      unattemptedPercentage: categories.unattempted?.percentage || 0,
    };
  }, [backendData]);

  useEffect(() => {
    if (refreshKey !== childRefreshKey) {
      setChildRefreshKey(refreshKey);
      refetch();
    }
  }, [refreshKey, childRefreshKey, refetch]);

  const handleExport = () => {
    toast.loading("Preparing export...", { id: "case-analytics-all-export" });
    exportHook.mutate(queryParams, {
      onSettled: () => toast.dismiss("case-analytics-all-export"),
    });
  };

  const handleFromDateChange = (newDate) => {
    setFromDate(newDate || dayjs().startOf("month"));
  };

  const handleToDateChange = (newDate) => {
    setToDate(newDate || dayjs().endOf("month"));
  };

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) setViewMode(newMode);
  };

  const handleDataTypeChange = (event) => {
    setLocalDataType(event.target.value);
    if (onDataTypeChange) onDataTypeChange(event.target.value);
  };

  const handleDataLabelsToggle = (event) =>
    setShowDataLabels(event.target.checked);

  const chartData = useMemo(() => {
    if (!data || Object.keys(data).length === 0)
      return { labels: [], datasets: [] };

    const suffix = viewMode === "percentage" ? "Percentage" : "";
    const labels = metricsConfig.map((m) => m.label);
    const values = metricsConfig.map((m) => data[m.key + suffix] || 0);
    const backgroundColors = metricsConfig.map((m) => m.backgroundColor);
    const borderColors = metricsConfig.map((m) => m.borderColor);

    return {
      labels,
      datasets: [
        {
          label: viewMode === "percentage" ? "Percentage (%)" : "Count",
          data: values,
          backgroundColor: backgroundColors,
          borderColor: borderColors,
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
        },
      ],
    };
  }, [data, viewMode]);

  const chartOptions = useMemo(
    () => ({
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      interaction: { intersect: false },
      layout: {
        padding: {
          right: showDataLabels ? 60 : 10,
          left: 10,
          top: 10,
          bottom: 10,
        },
      },
      plugins: {
        datalabels: {
          display: showDataLabels,
          align: "end",
          anchor: "end",
          color: "#333",
          font: {
            size: 11,
            weight: "bold",
            family: "Roboto, Arial, sans-serif",
          },
          formatter: (value) =>
            viewMode === "percentage"
              ? `${value.toFixed(1)}%`
              : value.toLocaleString(),
          clip: false,
          clamp: false,
          offset: 4,
        },
        legend: { display: false },
        title: {
          display: true,
          text: `${title} - ${fromDate.format("MMM YYYY")} to ${toDate.format("MMM YYYY")} (${viewMode === "percentage" ? "Percentage View" : "Count View"})`,
          font: chartStyles.fonts.title,
          padding: chartStyles.padding,
        },
        tooltip: {
          ...chartStyles.tooltip,
          callbacks: {
            label: (context) => {
              const value = context.parsed.x;
              const suffix = viewMode === "percentage" ? "%" : "";
              return `${context.dataset.label}: ${viewMode === "percentage" ? value.toFixed(2) : value.toLocaleString()}${suffix}`;
            },
          },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          grid: chartStyles.grid,
          ticks: {
            font: chartStyles.fonts.ticks,
            callback: (v) =>
              `${v.toLocaleString()}${viewMode === "percentage" ? "%" : ""}`,
          },
          title: {
            display: true,
            text: viewMode === "percentage" ? "Percentage (%)" : "Count",
            font: { size: 12, weight: "bold" },
          },
        },
        y: {
          grid: { display: false },
          ticks: {
            font: chartStyles.fonts.ticks,
            maxRotation: 0,
            minRotation: 0,
          },
        },
      },
      animation: chartStyles.animation,
      elements: { bar: { borderWidth: 1 } },
    }),
    [title, viewMode, showDataLabels],
  );

  return (
    <Paper
      elevation={2}
      sx={{ p: 3, border: "1px solid #d0d0d0", borderRadius: 2 }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <DateRangePicker
            fromDate={fromDate}
            toDate={toDate}
            onFromChange={handleFromDateChange}
            onToChange={handleToDateChange}
            disabled={isLoading}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="data-type-select-label">Data Type</InputLabel>
            <Select
              labelId="data-type-select-label"
              value={localDataType}
              label="Data Type"
              onChange={handleDataTypeChange}
            >
              {dataTypeOptions.map((opt) => (
                <MenuItem key={opt.label} value={opt.label}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={showDataLabels}
                onChange={handleDataLabelsToggle}
                size="small"
              />
            }
            label="Show Values"
            sx={{ ml: 1 }}
          />
        </Stack>{" "}
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="outlined"
            onClick={() => refetch()}
            disabled={isLoading}
            size="small"
          >
            {isLoading ? "Refreshing..." : "Refresh"}
          </Button>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
            sx={{
              "& .MuiToggleButton-root": { px: 2, py: 1, fontSize: "0.875rem" },
            }}
          >
            <ToggleButton value="count">Count View</ToggleButton>
            <ToggleButton value="percentage">Percentage View</ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlined />}
            onClick={handleExport}
            disabled={
              exportHook.isLoading || !data || Object.keys(data).length === 0
            }
            size="small"
            sx={{
              borderColor: "#1976d2",
              color: "#1976d2",
              "&:hover": { borderColor: "#1565c0", backgroundColor: "#f3f4f6" },
              "&:disabled": { borderColor: "#e0e0e0", color: "#9e9e9e" },
            }}
          >
            {exportHook.isLoading ? "Exporting..." : "Export"}
          </Button>
        </Stack>
      </Stack>

      {/* Disclaimer accordion explaining how counts are computed */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2" color="text.secondary">
            Why these counts? (click to expand)
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary">
            This counts is based on the latest case. Meaning if we filter by the
            month, then we will get the allocations where the latest allocation
            was done on that month and they have not been tried again in the
            future.
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Box sx={{ height: 500, width: '100%' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : chartData.labels.length > 0 ? <Bar data={chartData} options={chartOptions} redraw={false} /> : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
            <Typography variant="body1">No data available for the selected filters</Typography>
          </Box>
        )}

      </Box>

      {data && Object.keys(data).length > 0 && (
        <Box sx={{ mt: 2, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Data Type:</strong> {localDataType} |<strong> View:</strong>{" "}
            {viewMode === "percentage" ? "Percentage" : "Count"} |
            <strong> Labels:</strong> {showDataLabels ? "Visible" : "Hidden"} |
            <strong> Total Metrics:</strong> {metricsConfig.length}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default CaseDataTypeAllChart;
